import {consume} from "@lit/context";
import {t} from "i18next";
import {LitElement, nothing, unsafeCSS, type PropertyValues} from "lit";
import {customElement, query, state} from "lit/decorators.js";
import {classMap} from "lit/directives/class-map.js";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import {html, unsafeStatic} from "lit/static-html.js";
import Bars3 from "@interstate/icons-svg/bars-3.svg?raw";
import DocumentText from "@interstate/icons-svg/document-text.svg?raw";
import Home from "@interstate/icons-svg/home.svg?raw";
import XMark from "@interstate/icons-svg/x-mark.svg?raw";
import type {RuntimeEnvironment} from "@common-core/web-component-runtime/runtime";

import type {BridgeBarDataContextValue, BridgeUser} from "../../backend";
import {BridgeBarDataContext} from "../../backend";
import type {BridgeBarConfig} from "../../config";
import {BridgeBarConfigContext} from "../../config";
import {COLLAPSE_COMPONENTS_EVENT, ROOT_TAG} from "../../globals";
import {InterstateIcon} from "../../interstate";
import {features} from "../../launchdarkly";
import {RuntimeContext} from "../../runtime/RuntimeContext";
import type {Environment} from "../../runtime/Environment";
import styles from "./side-nav.css?inline";
import {
  fromSolutionLink,
  fromUserLink,
  hasUsableUri,
  sideNavUserLinks,
  type SideNavEntry
} from "./sideNavEntries";
import {SideNavScroller} from "./SideNavScrollerElement";
import {SideNavItem, type SideNavItemLayout} from "./SideNavItemElement";

export const tagName = `${ROOT_TAG}-side-nav`;
export const SideNav = unsafeStatic(tagName);

/** Read from the BFF-forwarded flags, not the LD client, so it matches the `user_links` the BFF gated on it. */
export const SIDE_NAV_ENABLED_FLAG = features.pdt.sideNav;

const OVERLAY_ID = `${ROOT_TAG}-side-nav-overlay`;
const MOBILE_MENU_ID = `${ROOT_TAG}-side-nav-mobile-menu`;
const MOBILE_TITLE_ID = `${ROOT_TAG}-side-nav-mobile-title`;

const HEADER_OFFSET_CLASS = "top-[35px]";

type SidebarVariant = "fixed" | "float";

const WRAPPER_CLASSES: Record<SidebarVariant, string> = {
  fixed: `ids-local-nav fixed left-0 ${HEADER_OFFSET_CLASS} bottom-0 z-[99998] w-[100px] max-w-[100vw] flex-col items-stretch border-r border-solid border-neutral-200 bg-white dark:border-primary-700 dark:bg-primary-900`,
  float: "mr-4"
};

/** Below this width the float overlay itself gives way to the mobile slide-over Menu. */
const MOBILE_VIEWPORT_QUERY = "(max-width: 799px)";

interface SideNavEntries {
  primary: SideNavEntry[];
  solutions: SideNavEntry[];
}

function isSelectedPage(href: string | undefined): boolean {
  if (!href) {
    return false;
  }
  try {
    return (
      new URL(href, window.location.href).origin === window.location.origin
    );
  } catch {
    return false;
  }
}

@customElement(tagName)
export class SideNavElement extends LitElement {
  static styles = unsafeCSS(styles);

  @consume({context: BridgeBarDataContext, subscribe: true})
  private dataContext: BridgeBarDataContextValue | undefined;

  @consume({context: BridgeBarConfigContext, subscribe: true})
  private config: BridgeBarConfig | undefined;

  @consume({context: RuntimeContext, subscribe: true})
  private runtime: RuntimeEnvironment<Environment, unknown> | undefined;

  @state()
  private isOpen = false;

  @state()
  private isMobileViewport = false;

  @query("[data-testid='side-nav-trigger']")
  private triggerButton?: HTMLButtonElement | null;

  @query("[data-testid='side-nav-mobile-close']")
  private closeButton?: HTMLButtonElement | null;

  @query("[data-testid='side-nav-fixed-rail']")
  private fixedRail?: HTMLElement | null;

  private mobileViewportQuery?: MediaQueryList;
  private savedBodyOverflow?: string;

  private handleDocumentClick = (e: MouseEvent) => {
    if (!e.composedPath().includes(this)) {
      this.closeOverlay();
    }
  };

  private handleDocumentKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Escape" && this.isOpen) {
      this.closeOverlay();
    }
  };

  private handleMenuKeyDown = (e: KeyboardEvent) => {
    if (e.key !== "Tab") return;
    const focusables = Array.from(
      this.renderRoot.querySelectorAll<HTMLElement>(
        `#${MOBILE_MENU_ID} a[href], #${MOBILE_MENU_ID} button:not([tabindex="-1"])`
      )
    );
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = (this.renderRoot as ShadowRoot).activeElement;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last?.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first?.focus();
    }
  };

  private handleCollapseRequest = () => {
    this.closeOverlay();
  };

  private handleMobileViewportChange = (e: MediaQueryListEvent) => {
    this.isMobileViewport = e.matches;
  };

  override connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener("click", this.handleDocumentClick);
    document.addEventListener("keydown", this.handleDocumentKeyDown);
    document.addEventListener(
      COLLAPSE_COMPONENTS_EVENT,
      this.handleCollapseRequest
    );
    this.mobileViewportQuery = window.matchMedia(MOBILE_VIEWPORT_QUERY);
    this.isMobileViewport = this.mobileViewportQuery.matches;
    this.mobileViewportQuery.addEventListener(
      "change",
      this.handleMobileViewportChange
    );
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener("click", this.handleDocumentClick);
    document.removeEventListener("keydown", this.handleDocumentKeyDown);
    document.removeEventListener(
      COLLAPSE_COMPONENTS_EVENT,
      this.handleCollapseRequest
    );
    this.mobileViewportQuery?.removeEventListener(
      "change",
      this.handleMobileViewportChange
    );
    this.isOpen = false;
    this.syncBodyScrollLock();
  }

  override willUpdate(changed: PropertyValues): void {
    const mode = this.config?.theme ?? "light";
    if (this.getAttribute("data-ids-theme-mode") !== mode) {
      this.setAttribute("data-ids-theme-mode", mode);
    }
    if (this.isOpen && (!this.isSideNavEnabled || this.surface === "fixed")) {
      this.closeOverlay();
    }
    if (
      this.isOpen &&
      changed.get("isMobileViewport") === true &&
      !this.isMobileViewport
    ) {
      this.closeOverlay();
    }
    this.syncBodyScrollLock();
  }

  private get bridgeUser(): BridgeUser | undefined {
    return this.dataContext?.status === "complete"
      ? this.dataContext.data
      : undefined;
  }

  private get isSideNavEnabled(): boolean {
    return (
      this.bridgeUser?.feature_flags?.[SIDE_NAV_ENABLED_FLAG]?.value === true
    );
  }

  private get sidebarVariant(): SidebarVariant {
    return this.config?.sidebarVariant ?? "fixed";
  }

  private get surface(): "fixed" | "float" | "mobile" {
    return this.isMobileViewport ? "mobile" : this.sidebarVariant;
  }

  private get entries(): SideNavEntries {
    const user = this.bridgeUser;
    const environment = this.runtime?.environment;
    const primary: SideNavEntry[] = [];

    if (environment?.landingPageUrl) {
      primary.push({
        testId: "side-nav-dashboard-link",
        href: environment.landingPageUrl,
        label: t("sideNav.dashboardLabel"),
        icon: {svg: Home, size: "md"}
      });
    }
    if (user) {
      primary.push(...sideNavUserLinks(user).map(fromUserLink));
    }
    if (environment?.productUpdatesUrl) {
      primary.push({
        testId: "side-nav-product-updates-link",
        href: environment.productUpdatesUrl,
        label: t("sideNav.productUpdatesLabel"),
        icon: {svg: DocumentText, size: "lg"}
      });
    }

    return {
      primary,
      solutions: (user?.solution_links ?? [])
        .filter(hasUsableUri)
        .map(fromSolutionLink)
    };
  }

  private handleTriggerClick(): void {
    if (this.isOpen) {
      this.closeOverlay();
    } else {
      this.openOverlay();
    }
  }

  private openOverlay(): void {
    this.isOpen = true;
    this.syncBodyScrollLock();
    if (this.surface === "mobile") {
      void this.updateComplete.then(() => this.closeButton?.focus());
    }
    this.emitToggle("component-expanded");
  }

  private closeOverlay(): void {
    if (!this.isOpen) return;
    const hadFocusInside =
      (this.renderRoot as ShadowRoot).activeElement != null;
    this.isOpen = false;
    this.syncBodyScrollLock();
    if (hadFocusInside) {
      /** The trigger is removed on this surface, so focus must move to the rail once it exists post-render, or it drops to <body>. */
      if (this.surface === "fixed") {
        void this.updateComplete.then(() => this.fixedRail?.focus());
      } else {
        this.triggerButton?.focus();
      }
    }
    this.emitToggle("component-collapsed");
  }

  private syncBodyScrollLock(): void {
    const shouldLock = this.isOpen && this.surface === "mobile";
    if (shouldLock && this.savedBodyOverflow === undefined) {
      this.savedBodyOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
    } else if (!shouldLock && this.savedBodyOverflow !== undefined) {
      document.body.style.overflow = this.savedBodyOverflow;
      this.savedBodyOverflow = undefined;
    }
  }

  private emitToggle(type: "component-expanded" | "component-collapsed") {
    this.dispatchEvent(
      new CustomEvent(type, {
        detail: {component: "side-nav"},
        composed: true,
        bubbles: true
      })
    );
  }

  private renderTrigger() {
    const surface = this.surface;
    return html`
      <button
        data-testid="side-nav-trigger"
        class="ids-btn dark:text-white dark:hover:bg-white/[12%] dark:focus:bg-white/[12%] dark:active:bg-white/[12%]"
        data-ids-variant="tertiary"
        data-ids-size="sm"
        aria-expanded=${this.isOpen ? "true" : "false"}
        aria-controls=${surface === "mobile" ? MOBILE_MENU_ID : OVERLAY_ID}
        aria-label=${t("sideNav.ariaLabel")}
        @click=${this.handleTriggerClick}>
        <${InterstateIcon} svg="${Bars3}" class="h-4 w-4 flex-shrink-0"></${InterstateIcon}>
        ${
          surface === "mobile"
            ? nothing
            : html`<span class="hidden md:block"
                >${unsafeHTML(t("sideNav.menuLabel"))}</span
              >`
        }
      </button>
    `;
  }

  private renderItems(layout: SideNavItemLayout, entries: SideNavEntries) {
    const isRail = layout === "rail";
    const renderEntry = (entry: SideNavEntry, selectable: boolean) =>
      html`<${SideNavItem}
        class="block"
        .entry=${entry}
        layout=${layout}
        .focusable=${isRail || this.isOpen}
        .selected=${selectable && isSelectedPage(entry.href)}
        @click=${isRail ? nothing : () => this.closeOverlay()}
      ></${SideNavItem}>`;
    const {primary, solutions} = entries;

    return html`
      ${primary.map(entry => renderEntry(entry, true))}
      ${solutions.length
        ? html`<div
            role="group"
            data-testid="side-nav-solutions"
            class="border-t border-solid border-neutral-200 mt-2 pt-2 dark:border-primary-700">
            ${solutions.map(entry => renderEntry(entry, false))}
          </div>`
        : nothing}
    `;
  }

  private renderScrollableItems(layout: SideNavItemLayout) {
    const entries = this.entries;
    return html`<${SideNavScroller}
      class="contents"
      .content=${this.renderItems(layout, entries)}
      layout=${layout}
      .contentRole=${layout === "overlay" ? "menu" : undefined}
      .showEndDivider=${entries.solutions.length > 0}
      .alwaysTabbable=${layout === "rail"}
      .isOpen=${this.isOpen}
    ></${SideNavScroller}>`;
  }

  /** Shared open/close slide-transform classes for the overlay and mobile Menu. */
  private slideTransformClasses() {
    return classMap({
      [HEADER_OFFSET_CLASS]: true,
      "translate-x-0": this.isOpen,
      "-translate-x-full": !this.isOpen,
      "pointer-events-none": !this.isOpen
    });
  }

  private renderOverlay() {
    return html`
      <aside
        id=${OVERLAY_ID}
        data-testid="side-nav-overlay"
        aria-hidden=${this.isOpen ? "false" : "true"}
        class="ids-slideout ids-slideout-left h-auto max-h-none bottom-0
          z-[99998] w-[100px] max-w-[100vw] p-0 dark:border-primary-700 dark:bg-primary-900
          ${this.slideTransformClasses()}">
        <div role="none" class="ids-slideout-body flex min-h-0 flex-1 flex-col">
          ${this.renderScrollableItems("overlay")}
        </div>
      </aside>
    `;
  }

  private renderFixedRail() {
    return html`
      <nav
        aria-label=${t("sideNav.railAriaLabel")}
        data-testid="side-nav-fixed-rail"
        tabindex="-1"
        class="flex flex-1 flex-col min-h-0 focus:outline-none">
        ${this.renderScrollableItems("rail")}
      </nav>
    `;
  }

  private renderMobileMenuHeader() {
    return html`<div
      class="ids-slideout-header flex flex-shrink-0 items-center justify-between gap-4 px-4 py-4">
      <h2
        id=${MOBILE_TITLE_ID}
        data-testid="side-nav-mobile-title"
        class="ids-slideout-title m-0 text-base font-semibold text-neutral-900 dark:text-white">
        ${t("sideNav.menuTitle")}
      </h2>
      <button
        data-testid="side-nav-mobile-close"
        class="ids-btn dark:!bg-transparent dark:!text-white dark:hover:!bg-white/[12%] dark:focus:!bg-white/[12%] dark:active:!bg-white/[12%]"
        data-ids-variant="close"
        data-ids-size="sm"
        tabindex=${this.isOpen ? nothing : "-1"}
        aria-label=${t("sideNav.menuClose")}
        @click=${() => this.closeOverlay()}>
        <${InterstateIcon} svg="${XMark}" class="h-4 w-4 flex-shrink-0"></${InterstateIcon}>
      </button>
    </div>`;
  }

  private renderMobileMenu() {
    return html`
      <aside
        id=${MOBILE_MENU_ID}
        data-testid="side-nav-mobile-menu"
        role="dialog"
        aria-modal="true"
        aria-labelledby=${MOBILE_TITLE_ID}
        aria-hidden=${this.isOpen ? "false" : "true"}
        ?inert=${!this.isOpen}
        @keydown=${this.handleMenuKeyDown}
        class="fixed left-0 bottom-0 w-[272px] min-w-[272px] max-w-[100vw]
          flex flex-col overflow-hidden border-r border-solid
          border-neutral-200 bg-white z-[99998] transition-transform
          duration-500 ease-in-out motion-reduce:transition-none
          dark:border-primary-700 dark:bg-primary-900
          ${this.slideTransformClasses()}">
        ${this.renderMobileMenuHeader()}
        ${this.renderScrollableItems("mobile-menu")}
      </aside>
    `;
  }

  private renderMobileScrim() {
    const classes = classMap({
      [HEADER_OFFSET_CLASS]: true,
      "opacity-100": this.isOpen,
      "opacity-0": !this.isOpen,
      "pointer-events-none": !this.isOpen
    });

    return html`<div
      data-testid="side-nav-mobile-scrim"
      class="ids-slideout-mask fixed inset-0 z-[99997] transition-opacity
        duration-500 ease-in-out motion-reduce:transition-none ${classes}"
      aria-hidden="true"
      @click=${() => this.closeOverlay()}></div>`;
  }

  override render() {
    if (!this.isSideNavEnabled) {
      return nothing;
    }

    const surface = this.surface;
    const isFixed = surface === "fixed";
    const variant: SidebarVariant = isFixed ? "fixed" : "float";
    return html`
      <div class=${WRAPPER_CLASSES[variant]} data-sidebar-variant=${variant}>
        ${isFixed
          ? this.renderFixedRail()
          : surface === "mobile"
            ? html`${this.renderTrigger()} ${this.renderMobileScrim()}
              ${this.renderMobileMenu()}`
            : html`${this.renderTrigger()} ${this.renderOverlay()}`}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [tagName]: SideNavElement;
  }
}
